import React, { ElementType, forwardRef, useMemo, useCallback } from 'react';
import { Box, BoxProps, SvgIcon, SvgIconProps, Breakpoint, useTheme } from '@mui/material';

interface LogoBaseProps extends Omit<SvgIconProps, 'color'> {
  /**
   * Specifies which color should be used
   * @default emerald in light mode, electric in dark mode
   */
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  color?: 'emerald' | 'electric' | 'white' | 'black' | string;
  /**
   * Width of the logo. Specify in px for subbrand to work properly
   * @default 120px
   */
  width?: number | string;
  /**
   * Height of the logo
   */
  height?: number | string;
}

interface LogoComponentProps extends LogoBaseProps {
  variant?: 'horizontal' | 'vertical' | 'symbol' | 'separated';
}

interface HorizontalLogoProps extends LogoBaseProps {
  variant?: 'horizontal';
  /**
   * Subbrand text next to the logo
   */
  subbrandText?: string;
  /**
   * Defines if subbrand should render next to the logo
   * @default horizontal
   */
  subbrandVariant?: 'horizontal' | 'vertical';
  /**
   * Properties applied to wrapper of subbrandText
   */
  subbrandProps?: BoxProps<ElementType>;
  /**
   * Breakpoint for hiding the Subbrand variant
   */
  breakpoint?: Breakpoint;
  /**
   * Properties applied to the wrapper of logo with subrandText
   */
  wrapperProps?: BoxProps<ElementType>;
}

interface VerticalLogoProps extends LogoBaseProps {
  variant: 'vertical';
}

interface SymbolLogoProps extends LogoBaseProps {
  variant: 'symbol';
}

interface SeparatedLogoProps extends LogoBaseProps {
  variant: 'separated';
  /**
   * Change spacing size between letter
   * @default 100
   */
  spacing?: number;
}

export type LogoProps = HorizontalLogoProps | VerticalLogoProps | SymbolLogoProps | SeparatedLogoProps;

/**
 * Logo of Škoda Auto a.s.
 */
export const Logo: React.FC<LogoProps> = forwardRef((props, ref) => {
  const Component = useMemo<React.FC<LogoProps>>(() => {
    switch (props.variant) {
      case 'separated':
        return SeparatedLogo;
      case 'symbol':
      case 'vertical':
        return LogoComponent;
      default:
        return HorizontalLogo;
    }
  }, [props.variant]);

  return <Component ref={ref} {...props} />;
});

const HorizontalLogo: React.FC<HorizontalLogoProps> = forwardRef(
  (
    {
      subbrandText,
      subbrandProps,
      subbrandVariant = 'horizontal',
      wrapperProps,
      breakpoint,
      width = '120px',
      height,
      ...rest
    },
    ref,
  ) => {
    const { sx: wrapperSx, ...restWrapper } = wrapperProps ?? {};
    const { sx: subbrandSx, ...restSubbrand } = subbrandProps ?? {};

    // obtain font size for he subbrand text based on the width
    const [subbrandFontSize, subbrandSpacing] = useMemo(() => {
      if (!width && !height) {
        return [0, 0];
      }
      // split value to number and unit, e.g. "100px" => [100, "px"]
      const processValue = (value: string | number): [number, string] => {
        const number = Number.parseFloat(`${value}`);
        const unit = `${value}`.replace(`${number}`, '') || 'px';
        return [number, unit];
      };
      // obtain height and width value and units
      let widthNumber: number, widthUnit: string, heightNumber: number, heightUnit: string;
      if (width) {
        [widthNumber, widthUnit] = processValue(width);
        // obtain height from width (15:2 ratio)
        [heightNumber, heightUnit] = processValue(`${widthNumber / 7.5}${widthUnit}`);
      } else {
        [heightNumber, heightUnit] = processValue(height);
        // obtain width from height (2:15 ratio)
        [widthNumber, widthUnit] = processValue(`${heightNumber * 7.5}${heightUnit}`);
      }
      // calculate subbrand font size and space
      return [
        // magic number making sure the capitalized letter is aligned with logo height
        `${(widthNumber / 5.29).toFixed(2)}${widthUnit}`,
        // 1/2 of height or 1/4 of height
        `${(heightNumber / (subbrandVariant === 'vertical' ? 4 : 2)).toFixed(2)}${heightUnit}`,
      ];
    }, [width, height, subbrandVariant]);

    if (subbrandText && subbrandFontSize) {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            justifyContent: 'flex-start',
            alignItems: subbrandVariant === 'vertical' ? 'flex-start' : 'baseline',
            flexDirection: subbrandVariant === 'vertical' ? 'column' : 'row',
            ...wrapperSx,
          }}
          data-testid="Logo"
          {...restWrapper}
        >
          <LogoComponent width={width} height={height} ref={ref} {...rest} />
          <Box
            sx={{
              userSelect: 'none',
              whiteSpace: 'nowrap',
              fontWeight: '300',
              letterSpacing: '0.02em',
              fontSize: subbrandFontSize,
              ...(subbrandVariant === 'vertical'
                ? {
                    mt: subbrandSpacing,
                    lineHeight: '1',
                  }
                : {
                    ml: subbrandSpacing,
                    // do not increase height of the logo
                    lineHeight: '0px',
                  }),
              // hide subbrand with breakpoint
              ...(breakpoint && {
                display: { xs: 'none', [breakpoint]: 'inline' },
              }),
              ...subbrandSx,
            }}
            component="span"
            data-testid="Logo.subbrand"
            {...restSubbrand}
          >
            {subbrandText}
          </Box>
        </Box>
      );
    } else {
      return <LogoComponent width={width} height={height} ref={ref} {...rest} />;
    }
  },
);

const SeparatedLogo: React.FC<SeparatedLogoProps> = forwardRef(({ sx, spacing = 100, ...rest }, ref) => {
  // function to get proper transform for each path, rounded to one decimal
  const getTranslateX = useCallback(
    (i: number, sp: number) =>
      // values were manually mapped for each letter
      `translateX(${Math.round(([0, -44.1667, -22.5, 0, 24.5, 46.5][i] || 0) * (sp / 100 - 1) * 100) / 100}%)`,
    [],
  );

  return (
    <LogoComponent
      sx={{
        transition: 'opacity 1s ease-in-out',
        '& path': {
          transform: 'translateZ(0, 0, 0)',
          transition: 'transform 1s ease-in-out',
        },
        // "s" letter
        '& path:nth-of-type(1)': { transform: getTranslateX(1, spacing) },
        // "k" letter
        '& path:nth-of-type(2)': { transform: getTranslateX(2, spacing) },
        // "o" never moves
        // "d" letter
        '& path:nth-of-type(4)': { transform: getTranslateX(4, spacing) },
        // "a" letter
        '& path:nth-of-type(5)': { transform: getTranslateX(5, spacing) },
        ...sx,
      }}
      ref={ref}
      {...rest}
    />
  );
});

const LogoComponent: React.FC<LogoComponentProps> = forwardRef(
  ({ sx, width = '120px', height, color, variant = 'horizontal', ...rest }, ref) => {
    const theme = useTheme();
    // obtain component based on the variant
    const Component = useMemo(() => {
      switch (variant) {
        case 'symbol':
          return SymbolLogoComponent;
        case 'vertical':
          return VerticalLogoComponent;
        case 'separated':
          return SeparatedHorizontalLogoComponent;
        default:
          return HorizontalLogoComponent;
      }
    }, [variant]);
    // obtain color
    const logoColor = useMemo(() => {
      switch (color) {
        case undefined:
          return theme.palette.mode === 'dark' ? 'brand.secondary' : 'brand.tertiary';
        case 'emerald':
          return 'brand.tertiary';
        case 'electric':
          return 'brand.secondary';
        case 'white':
          return '#fff';
        case 'black':
          return '#000';
        default:
          return color;
      }
    }, [theme.palette.mode, color]);

    return (
      <Component
        sx={{
          width: width || 'unset',
          height: height || 'unset',
          fontSize: 'unset',
          '&, & + span': {
            color: logoColor,
          },
          ...sx,
        }}
        data-testid={`Logo.${variant || 'horizontal'}`}
        ref={ref}
        {...rest}
      />
    );
  },
);

const SymbolLogoComponent: React.FC<SvgIconProps> = forwardRef((props, ref) => {
  return (
    <SvgIcon viewBox="0 0 32 32" ref={ref} {...props}>
      <path d="M16 0C7.16345 0 0 7.16345 0 16C0 24.8366 7.16345 32 16 32C24.8366 32 32 24.8366 32 16C32 7.16345 24.8366 0 16 0ZM16 30.6181C7.92664 30.6181 1.38188 24.0734 1.38188 16C1.38188 7.92664 7.92664 1.38188 16 1.38188C24.0734 1.38188 30.6181 7.92664 30.6181 16C30.6181 24.0734 24.0734 30.6181 16 30.6181ZM22.9186 20.9748L22.5599 21.9432H21.2481C21.5987 20.998 26.0191 10.4529 17.3331 3.30067C15.5096 3.10748 13.666 3.31141 11.9289 3.89843C12.2186 4.1318 15.2275 6.60184 16.6453 9.65167L16.2293 9.76367C15.4478 8.91211 12.6085 5.78599 9.90293 4.7799C8.44709 5.57356 7.15898 6.64201 6.10992 7.92608C7.3806 8.57612 10.7008 10.5592 11.9627 12.0997C11.8904 12.1322 11.6067 12.285 11.6067 12.285C9.29207 10.5258 5.18018 9.61266 4.97001 9.56732C4.1056 11.0449 3.54923 12.6821 3.33443 14.3804C9.70223 15.6621 11.4168 18.6091 11.7101 21.9432H10.504L8.43136 21.0398C8.32338 20.9993 8.2099 20.9754 8.09477 20.9689H4.23562C4.84653 22.4112 5.7173 23.7288 6.80453 24.8563H8.44907C8.59079 24.8563 8.7148 24.8386 8.8388 24.7677L10.504 23.9174H22.5599L22.9186 24.8859C24.4165 24.7132 25.8066 24.0213 26.8475 22.9303C25.8066 21.8394 24.4165 21.1475 22.9186 20.9748ZM18.2763 20.4696C17.9639 20.4696 17.6585 20.3769 17.3987 20.2033C17.1389 20.0298 16.9365 19.7831 16.8169 19.4944C16.6974 19.2058 16.6661 18.8881 16.727 18.5817C16.788 18.2753 16.9384 17.9938 17.1593 17.7729C17.3803 17.552 17.6617 17.4015 17.9682 17.3406C18.2746 17.2796 18.5922 17.3109 18.8808 17.4305C19.1695 17.55 19.4162 17.7525 19.5898 18.0123C19.7634 18.2721 19.856 18.5775 19.856 18.8899C19.856 19.3088 19.6896 19.7106 19.3933 20.0069C19.0971 20.3031 18.6953 20.4696 18.2763 20.4696Z" />
    </SvgIcon>
  );
});

const VerticalLogoComponent: React.FC<SvgIconProps> = forwardRef((props, ref) => {
  return (
    <SvgIcon viewBox="0 0 138 130" ref={ref} {...props}>
      <path d="M66 21C51.6406 21 40 32.6406 40 47C40 61.3594 51.6406 73 66 73C80.3594 73 92 61.3594 92 47C92 32.6406 80.3594 21 66 21ZM66 70.7544C52.8808 70.7544 42.2456 60.1192 42.2456 47C42.2456 33.8808 52.8808 23.2456 66 23.2456C79.1192 23.2456 89.7544 33.8808 89.7544 47C89.7544 60.1192 79.1192 70.7544 66 70.7544ZM77.2427 55.084L76.6598 56.6578H74.5282C75.0979 55.1218 82.281 37.9859 68.1662 26.3636C65.2032 26.0497 62.2073 26.381 59.3845 27.335C59.8553 27.7142 64.7448 31.728 67.0487 36.684L66.3727 36.866C65.1026 35.4822 60.4889 30.4023 56.0923 28.7673C53.7265 30.057 51.6333 31.7933 49.9286 33.8799C51.9935 34.9361 57.3888 38.1586 59.4393 40.6619C59.3219 40.7147 58.8608 40.9631 58.8608 40.9631C55.0996 38.1043 48.4178 36.6205 48.0763 36.5469C46.6716 38.9479 45.7675 41.6084 45.4184 44.3681C55.7661 46.4509 58.5524 51.2397 59.0289 56.6578H57.069L53.701 55.1897C53.5255 55.1239 53.3411 55.0851 53.154 55.0745H46.8829C47.8756 57.4181 49.2906 59.5594 51.0574 61.3915H53.7297C53.95 61.4023 54.1691 61.3525 54.363 61.2476L57.069 59.8658H76.6598L77.2427 61.4396C79.6768 61.1589 81.9357 60.0345 83.6271 58.2618C81.9357 56.4891 79.6768 55.3647 77.2427 55.084ZM69.699 54.263C69.1913 54.263 68.695 54.1125 68.2729 53.8304C67.8508 53.5483 67.5218 53.1474 67.3275 52.6784C67.1332 52.2093 67.0824 51.6932 67.1814 51.1953C67.2805 50.6973 67.5249 50.24 67.8839 49.881C68.2429 49.522 68.7003 49.2775 69.1982 49.1785C69.6962 49.0794 70.2123 49.1302 70.6814 49.3245C71.1504 49.5188 71.5513 49.8478 71.8334 50.27C72.1154 50.6921 72.266 51.1884 72.266 51.6961C72.266 52.3769 71.9955 53.0298 71.5141 53.5112C71.0327 53.9926 70.3798 54.263 69.699 54.263H69.699Z" />
      <path d="M10 101L12.1675 97.6125H24.739C25.4511 97.6125 25.9156 97.1587 25.9156 96.4631V94.4971H15.1091C11.8888 94.4971 10.4645 93.1059 10.4645 90.0208V88.69C10.4645 86.3913 11.8579 85 14.0563 85H14.304V89.9603C14.304 90.6559 14.7685 91.1096 15.4807 91.1096H25.9466C28.3308 91.1096 29.7552 92.5009 29.7552 94.7694V96.5236C29.7552 99.5179 28.2379 101 25.1105 101L10 101ZM85.3338 97.552H95.0565C96.6666 97.552 97.6885 96.5841 97.6885 95.0718V90.9282C97.6885 89.4159 96.6666 88.448 95.0565 88.448H85.3338V97.552ZM81.4323 101V85H95.1494C99.2367 85 101.59 87.2987 101.59 91.1096V94.8903C101.59 98.7013 99.2367 101 95.1494 101H81.4323ZM62.5252 97.552H70.7308C72.217 97.552 73.1769 96.6446 73.1769 95.2231V90.7769C73.1769 89.3554 72.217 88.448 70.7308 88.448H62.5252C61.039 88.448 60.0791 89.3554 60.0791 90.7769V95.223C60.0791 96.6446 61.039 97.552 62.5252 97.552ZM70.8236 101H62.4324C58.469 101 56.1776 98.7618 56.1776 95.0416V90.9584C56.1776 87.2382 58.4689 85 62.4324 85H70.8236C74.787 85 77.0784 87.2382 77.0784 90.9584V95.0416C77.0784 98.7618 74.7871 101 70.8236 101ZM103.189 101L112.385 85H117.804L127 101H122.541L120.962 98.2476H113.128L111.022 94.9811L107.554 101H103.189ZM111.115 94.7996H118.98L115.048 87.9943L111.115 94.7996ZM18.1436 88.327L15.9142 85H29.3836L27.2161 88.327H18.1436ZM34.0483 101V85H37.9498V91.2306H43.1208L49.8401 85H55.0111L46.465 92.9546L55.1659 101H49.6852L42.966 94.6786H37.9498V101H34.0483Z" />
    </SvgIcon>
  );
});

const HorizontalLogoComponent: React.FC<SvgIconProps> = forwardRef((props, ref) => {
  return (
    <SvgIcon viewBox="0 0 120 16" ref={ref} {...props}>
      <path d="M0 16L2.22307 12.6125H15.1169C15.8473 12.6125 16.3237 12.1587 16.3237 11.4631V9.49713H5.24009C1.93724 9.49713 0.4764 8.10585 0.4764 5.02078V3.68997C0.4764 1.3913 1.90549 0 4.16032 0H4.41438V4.96029C4.41438 5.65595 4.89078 6.10964 5.6212 6.10964H16.3554C18.8008 6.10964 20.2617 7.50094 20.2617 9.76936V11.5236C20.2617 14.5179 18.7055 16 15.498 16L0 16ZM77.2654 12.552H87.2374C88.8889 12.552 89.9369 11.5841 89.9369 10.0718V5.92816C89.9369 4.41587 88.8889 3.44801 87.2374 3.44801H77.2654V12.552ZM73.2639 16V0H87.3328C91.5248 0 93.9385 2.29867 93.9385 6.10964V9.89034C93.9385 13.7013 91.5248 16 87.3328 16H73.2639ZM53.872 12.552H62.288C63.8123 12.552 64.7968 11.6446 64.7968 10.2231V5.77693C64.7968 4.35538 63.8123 3.44801 62.288 3.44801H53.872C52.3477 3.44801 51.3632 4.35538 51.3632 5.77693V10.223C51.3632 11.6446 52.3477 12.552 53.872 12.552ZM62.3832 16H53.7768C49.7118 16 47.3616 13.7618 47.3616 10.0416V5.9584C47.3616 2.23818 49.7117 0 53.7768 0H62.3832C66.4482 0 68.7984 2.23818 68.7984 5.9584V10.0416C68.7984 13.7618 66.4483 16 62.3832 16ZM95.578 16L105.01 0H110.568L120 16H115.427L113.807 13.2476H105.772L103.613 9.98108L100.056 16H95.578ZM103.708 9.79961H111.775L107.741 2.99428L103.708 9.79961ZM8.3524 3.32702L6.06581 0H19.8806L17.6575 3.32702H8.3524ZM24.6649 16V0H28.6665V6.23062H33.9701L40.8616 0H46.1652L37.4 7.95462L46.324 16H40.7028L33.8113 9.67863H28.6665V16H24.6649Z" />
    </SvgIcon>
  );
});

const SeparatedHorizontalLogoComponent: React.FC<SvgIconProps> = forwardRef((props, ref) => {
  return (
    <SvgIcon viewBox="0 0 120 16" ref={ref} {...props}>
      <path d="m0 16 2.2231-3.3875h12.894c0.7304 0 1.2068-0.4538 1.2068-1.1494v-1.966h-11.084c-3.3028 0-4.7637-1.3913-4.7637-4.4764v-1.3308c0-2.2987 1.4291-3.69 3.6839-3.69h0.25406v4.9603c0 0.69566 0.4764 1.1494 1.2068 1.1494h10.734c2.4454 0 3.9063 1.3913 3.9063 3.6597v1.7542c0 2.9943-1.5562 4.4764-4.7637 4.4764zm8.3524-12.673-2.2866-3.327h13.815l-2.2231 3.327z" />
      <path d="m24.665 16v-16h4.0016v6.2306h5.3036l6.8915-6.2306h5.3036l-8.7652 7.9546 8.924 8.0454h-5.6212l-6.8915-6.3214h-5.1448v6.3214z" />
      <path d="m53.872 12.552h8.416c1.5243 0 2.5088-0.9074 2.5088-2.3289v-4.4462c0-1.4216-0.9845-2.3289-2.5088-2.3289h-8.416c-1.5243 0-2.5088 0.90737-2.5088 2.3289v4.4461c0 1.4216 0.9845 2.329 2.5088 2.329zm8.5112 3.448h-8.6064c-4.065 0-6.4152-2.2382-6.4152-5.9584v-4.0832c0-3.7202 2.3501-5.9584 6.4152-5.9584h8.6064c4.065 0 6.4152 2.2382 6.4152 5.9584v4.0832c0 3.7202-2.3501 5.9584-6.4152 5.9584z" />
      <path d="m77.265 12.552h9.972c1.6515 0 2.6995-0.9679 2.6995-2.4802v-4.1436c0-1.5123-1.048-2.4802-2.6995-2.4802h-9.972zm-4.0015 3.448v-16h14.069c4.192 0 6.6057 2.2987 6.6057 6.1096v3.7807c0 3.811-2.4137 6.1097-6.6057 6.1097z" />
      <path d="m95.578 16 9.432-16h5.558l9.432 16h-4.573l-1.62-2.7524h-8.035l-2.159-3.2665-3.557 6.0189zm8.13-6.2004h8.067l-4.034-6.8053z" />
    </SvgIcon>
  );
});
